import { CONFIGURABLE_IMAGES, UTRADE_ONE_AI_PATH } from "@app/_helpers";
import { AIAssistantLevel, AIAssistantType } from "../../../../../utProto/webEnums_pb";
import { AIAssistantModel, OneAiRemoteConfig, PortfolioArtifactState, StrategisRemotetConfig, WidgetNavLink } from "./chat.interface";
import { AdvisorRemoteConfig } from './chat.interface';

export enum ResponseType {
    Text = 'text',
    Yaml = 'yaml',
    Screener = 'screener_id',
    Artifact = 'artifact',
    FollowUp = 'follow_up_prompts',
    Portfolio = 'portfolio',
}

export interface PortfolioSocketObj {
    portfolio_id: string;
    portfolio_version: string;
    is_preview?: boolean;
    auto_promoted?: boolean;
}

export interface PortfolioSocketUpdate {
    algo_id: string;
    message?: string;
    update?: 'delete';
}

export interface ParsedResponse {
    type: ResponseType;
    value: string;
    artifactKey?: string;
    artifact?: PortfolioArtifactState;
    wordCount?: number;
    prompts?: string[];
}

/** Language tags after opening ``` that get structured handling; any other tag (or no tag) is shown as plain text. */
export const CHAT_STRUCTURED_RESPONSE_FENCE_TAGS = new Set<string>([
    ResponseType.Yaml,
    ResponseType.Portfolio,
    ResponseType.Screener,
    ResponseType.FollowUp,
]);

export enum MessageSender {
    USER = 'user',
    AI = 'assistant',
}

export enum MessageType {
    Text = 'text',
    Audio = 'audio',
    Image = 'image_file',
    Prohibited = 'prohibited',
}

export enum SocketUpdateType {
    ThreadRunStart,
    ReceivedMessageDelta,
    ReceivedCompleteMessage,
    ReceivedRequiredAction,
    ThreadRunComplete,
    ThinkingStateUpdate,
    ReasoningUpdate,
    StreamError,
}

export enum EditorTabs {
    Basic,
    Advanced,
}

export enum AiPortfolioCommands {
    Deploy = 'Deploy',
    Backtest = 'Backtest',
    PayoffCurve = 'Payoff Curve',
    Overview = 'portfolioOverview',
    MarginCalculator = 'Margin Calculator'
}

/** Query param on portfolio-overview for AI flows (deploy, payoff curve, etc.); bumps the URL so overview reloads when already open. */
export const AI_PORTFOLIO_DEPLOY_INTENT_QUERY = 'aiDeployIntent';

export enum AiAssistantModel {
    OneAi = 'OneAi',
    Strategist = 'AiStrategist',
    UtradeOriginals = 'UtradeOriginals',
    AiScreener = 'AiScreener',
}

export const CacheThreadKey = 'aiThreads';
export const NoResponseText = 'I apologize but I am unable to comprehend your query properly. Can you please elaborate a little bit more?';

export const SavedPortfolioMessage = (portfolioName: string) => `${portfolioName} saved successfully`;

export const AI_ASSISTANT_TYPES: AIAssistantModel[] = [
    {
        id: AiAssistantModel.OneAi,
        label: 'Auto',
        value: AIAssistantType.AIASSISTANTTYPE_ONE_AI,
        icon: 'assets/icons/Auto.svg',
        selectedIcon: 'assets/icons/Auto_white.svg',
    },
    {
        id: AiAssistantModel.Strategist,
        label: "AI Strategy Builder",
        value: AIAssistantType.AIASSISTANTTYPE_STRATEGIST,
        icon: 'assets/icons/StrategyBuilder.svg',
        selectedIcon: 'assets/icons/StrategyBuilder_white.svg',
    },
    {
        id: AiAssistantModel.UtradeOriginals,
        label: "uTrade Originals",
        value: AIAssistantType.AIASSISTANTTYPE_UTRADEORIGINALHELPER,
        icon: 'assets/icons/Advisor.svg',
        selectedIcon: 'assets/icons/Advisor_white.svg',
    },
    {
        id: AiAssistantModel.AiScreener,
        label: "AI Screener",
        value: AIAssistantType.AIASSISTANTTYPE_SCREENER_CREATOR,
        icon: 'assets/icons/Screener.svg',
        selectedIcon: 'assets/icons/Screener_white.svg',
    },
]

export const AI_LOGO = CONFIGURABLE_IMAGES.aiLogo;
export const AI_SPARKLE_LOGO = 'assets/icons/ai-sparkle.svg';
export const ALGOS_LOGO = 'assets/icons/uAlgos.svg';

export const PRIVACY_POLICY_URL = 'https://utradealgos.com/privacy-policy/';
export const TERMS_OF_SERVICE_URL = 'https://utradealgos.com/terms-and-conditions/';

export const WIDGET_NAV_LINKS: WidgetNavLink[] = [
    {
        name: 'Plan and Pricing',
        route: '/trading/profile/user/plans',
    },
    {
        name: 'User Manual',
        route: '/trading/profile/help',
    },
    {
        name: 'Create Strategy',
        route: UTRADE_ONE_AI_PATH,
    },
    {
        name: 'Contact Us',
        route: '/trading/profile/help',
    }
]

export const SHRINKED_WIDGET_WIDTH = '360px';
export const EXPANDED_WIDGET_WIDTH = 'calc(50vw - 32px)';
export const WIDGET_HEIGHT = 'calc(100vh - 184px)';

export const AI_ASSISTANT_LEVELS = {
    'Default': AIAssistantLevel.DEFAULT,
    'Basic': AIAssistantLevel.WEAK,
    'Intermediate': AIAssistantLevel.MEDIUM,
    'Advanced': AIAssistantLevel.STRONG
}

export const AI_ASSISTANT_LEVEL_VALUES = {
    [AIAssistantLevel.DEFAULT]: 'Default',
    [AIAssistantLevel.WEAK]: 'Basic',
    [AIAssistantLevel.MEDIUM]: 'Intermediate',
    [AIAssistantLevel.STRONG]: 'Advanced'
}

export enum RequiredActions {
    Backtest = 'show_backtest',
    Payoff = 'show_payoffcurve',
    MarginCalc = 'show_margincalculator',
    ThreadTitleUpdate = 'update_thread_title',
    ConnectBroker = 'connect_broker'
}

export const AiDisclaimerPoints = [
    "AI-Generated Content: Information and analyses are AI-generated and may contain errors or biases.",
    "Not Financial Advice: This is for informational purposes only, not professional financial advice.",
    "No Guarantees: Past performance and backtesting results don't guarantee future outcomes.",
    "User Responsibility: You are solely responsible for your trading decisions. Consult qualified professionals before investing.",
    "Risk Warning: Trading carries risks, including potential loss of capital. Only trade what you can afford to lose.",
    "Technology Limitations: AI, Algo and Automated trading systems may have unforeseen errors or limitations.",
    "Legal Compliance: Ensure your use of this product complies with applicable laws and regulations."
]

export const ChatRegex = {
    followingPrompts: /(would you like to|Would you like to|select your response|Select your response):[\s\S]*?((?:\d+\.\s*.+[\s\S]*?)+)$/,
    numberPrefix: /^\d+\./,
    cleanPrompt: /^\d+\.\s*/,
    questionMark: /\?$/,
    marginHumanize: /{{(\d+_)?margin_humanize}}/g,
    smartDay: /{{smart_day}}/g,
    recommendedStrategies: /\|.*\|.*\|.*\|\n\|[-\s|]*\n((?:\|[^|\n]*\|[^|\n]*\|[^|\n]*\|\n)*)/,
    capitalValue: /[₹,]/g,
    allocatedCapital: /Total Capital Allocated.*?₹([\d,]+)/,
    remainingCapital: /Remaining Capital.*?₹([\d,]+)/,
    /** Optional fence label: ```yaml ...```, ```typescript ...```, or ```\n...``` (no label). */
    chatResponse: /```(?:(\w+)\s*)?([\s\S]*?)```/g,
    screener: /["']?id["']?\s*:\s*["']([^"']+)["']/,
    linkRegex: /<a\b[^>]*>([\s\S]*?)<\/a>/gi,
}

export const AdvisorThreadErrorStatus = 404;

export const AdvisorConfig: AdvisorRemoteConfig = {
    name: "uTrade AI Assistant",
    description: "A specialized AI assistant for uTrade Originals, focused on optimizing your capital allocation with tailored strategy suggestions to maximize returns and manage your capital efficiently.",
    config: {
        min_funds: 95000,
        timeout: 60,
        enable_input: true
    },
    initial_prompts: [
        {
            title: "Allocate {{margin_humanize}} for {{smart_day}}",
            description: "Suggest allocation for ₹ {{margin_humanize}} in uTrade Originals for {{smart_day}}"
        },
        {
            title: "Distribute {{75_margin_humanize}} for {{smart_day}}",
            description: "Recommend a portfolio distribution for ₹ {{75_margin_humanize}} in uTrade Originals for {{smart_day}}"
        },
        {
            title: "Distibute {{125_margin_humanize}} for {{smart_day}}",
            description: "Advise a portfolio distribution for ₹ {{125_margin_humanize}} in uTrade Originals for {{smart_day}}"
        }
    ],
    ctas: {
        multi_deploy: false,
        ai_backtest: true
    }
};

export const greetingStr = (name: string): string => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
        return `Good Morning ${name}! `;
    } else if (currentHour < 18) {
        return `Good Afternoon ${name}! `;
    } else {
        return `Good Evening ${name}! `;
    }
}

export const StrategistConfig: StrategisRemotetConfig = {
    timeout: 120,
    maxActiveThreads: 5,
    disclaimer: "Disclaimer: uTrade Intelligence may make mistakes. Please review the responses before making any trading decisions.",
    introduction: "What will you build today? Here are some starting points from where you can start building your trading strategy with AI.",
    initialPrompts: [
        "Create a basic NIFTY options portfolio for a bullish market outlook with risk management",
        "Design a simple BANKNIFTY options portfolio for a bearish market",
        "Develop a basic iron condor strategy for NIFTY options, assuming a sideways market",
        "Create a basic NIFTY straddle strategy for a high volatility market",
        "Develop a basic trend-following strategy for NIFTY options. Include a leg to buy 1 lot of ATM call when the 20-period EMA crosses above the 50-period EMA, and exit when it crosses below"
    ],
    waitingPrompts: [
        'Analyzing your input...',
        'Gathering relevant data...',
        'Processing information...',
        'Formulating a response...',
        'Almost there, please wait...'
    ]
}

export const OneAiConfig: OneAiRemoteConfig = {
    timeout: 120,
    disclaimer: "Disclaimer: uTrade Intelligence may make mistakes. Please review the responses before making any trading decisions.",
    initial_prompts: [
        {
            title: "What Can You Do for Me?",
            subtitle: "Discover all AI superpowers",
            description: "You're my AI trading assistant. Tell me everything you can help me with - market analysis, stock research, screeners, strategies, IPOs, and more. What should I try first?",
            icon: "auto_awesome"
        },
        {
            title: "What's Moving Right Now?",
            subtitle: "Live market sentiment + FII/DII action",
            description: "Give me a quick market pulse - What's the sentiment today? How are FII/DII positioned? Is VIX showing fear or greed? What should I watch out for?",
            icon: "speed"
        },
        {
            title: "Show Me Hot IPOs",
            subtitle: "Open, upcoming + grey market buzz",
            description: "Show me all IPOs - what's open now, what's coming up, and what just listed. Include price bands, dates, and which ones are getting the most buzz.",
            icon: "rocket_launch"
        },
        {
            title: "What's Risky Today?",
            subtitle: "VIX, volatility & warning signs",
            description: "Give me a risk check - Is VIX high? Any major events coming up? What should I be cautious about before trading today?",
            icon: "gpp_maybe"
        },
        {
            title: "Build a NIFTY Strategy",
            subtitle: "Options portfolio in 60 seconds",
            description: "Create a NIFTY options strategy for me. I want something with limited risk that can profit if the market goes up slightly. Explain the setup simply.",
            icon: "architecture"
        },
        {
            title: "Find Me Winning Stocks",
            subtitle: "AI-powered stock discovery",
            description: "I want to find high-growth stocks with strong fundamentals. Create a screener for companies with good revenue growth, healthy margins, and low debt. Show me the top picks.",
            icon: "emoji_events"
        },
        {
            title: "Deep Dive Any Stock",
            subtitle: "Fundamentals + technicals in one go",
            description: "I'm considering investing in a stock and want your help analyzing it. Walk me through what you'd need to know, then give me the full picture - financials, news, and technicals.",
            icon: "manage_search"
        },
        {
            title: "Build a BANKNIFTY Strategy",
            subtitle: "High-action index, quick setups",
            description: "Create a BANKNIFTY options strategy for me. I want something that works well for intraday trading with defined risk.",
            icon: "flash_on"
        },
        {
            title: "Where Should I Put My Money?",
            subtitle: "Smart allocation across strategies",
            description: "I have some capital to invest in uTrade Originals. Recommend how I should distribute it across different strategies based on current market conditions.",
            icon: "savings"
        },
        {
            title: "Which Sector is Hot Right Now?",
            subtitle: "Sector rotation insights",
            description: "Tell me which sectors are performing well and which are lagging. What's driving the momentum and where should I focus?",
            icon: "local_fire_department"
        },
        {
            title: "Compare Two Stocks for Me",
            subtitle: "Head-to-head analysis",
            description: "I want to compare two stocks before investing. Help me understand which one is better - just ask me which stocks to compare.",
            icon: "compare_arrows"
        },
        {
            title: "Build Me an Equity Strategy",
            subtitle: "Stock trading made simple",
            description: "Create an equity trading strategy for me. I want to trade stocks (not options) with clear entry and exit rules.",
            icon: "candlestick_chart"
        }
    ],
    fixedPromptsHeading: "What would you like to explore today?",
    enableAiModels: {
        AiStrategist: true,
        UtradeOriginals: true,
        AiScreener: true
    }
}

export enum FEEDBACK_TYPE {
    GOOD = 'thumbs_up',
    BAD = 'thumbs_down'
}

export const BAD_FEEDBACK_TYPES = [
    {
        type: 'inaccurate',
        message: 'Information was inaccurate'
    },
    {
        type: 'incomplete',
        message: 'Response was incomplete'
    },
    {
        type: 'irrelevant',
        message: 'Response was irrelevant to my query'
    },
    {
        type: 'unclear',
        message: 'Instructions or explanations were unclear'
    },
    {
        type: 'strategy',
        message: 'Trading strategy was not suitable'
    },
    {
        type: 'technical',
        message: 'Technical/calculation errors'
    },
    {
        type: 'other',
        message: 'Other issue'
    }
]

export const ActionNames = {
    GetCompanyStockAnalysis: 'get_company_stock_analysis',
    SearchEquitySymbols: 'search_equity_symbols',
    ListScreeners: 'list_screeners',
    GetScreenerData: 'get_screener_data',
    CreateScreenerFromNl: 'create_screener_from_nl',
    GetScreenerCreationStatus: 'get_screener_creation_status',
    GetSymbolsStrikesInfo: 'get_symbols_strikes_info',
    GetLtp: 'get_ltp',
    GetOi: 'get_oi',
    GetSymbolsStrikesPcrMaxPain: 'get_symbols_strikes_pcr_max_pain',
    GetScripDetailsByIsin: 'get_scrip_details_by_isin',
    SwitchAssisstant: 'switch_assistant',
    Sleep: 'sleep',
    UpdateThreadTitle: 'update_thread_title',
    ValidatePortfolYML: 'validate_portfolio_yml'
}

export interface ThinkingMessage {
    header: string;
    description: string;
}


export const THINKING_MESSAGES: Record<string, ThinkingMessage> = {
    default: {
        header: "Processing Request",
        description: "Working on your query"
    },
    [ActionNames.GetCompanyStockAnalysis]: {
        header: "Analyzing Stock",
        description: "Fetching company stock analysis data"
    },
    [ActionNames.SearchEquitySymbols]: {
        header: "Searching Symbols",
        description: "Looking for matching equity symbols"
    },
    [ActionNames.ListScreeners]: {
        header: "Loading Screeners",
        description: "Fetching available screeners from database"
    },
    [ActionNames.GetScreenerData]: {
        header: "Loading Data",
        description: "Retrieving screener results and criteria"
    },
    [ActionNames.CreateScreenerFromNl]: {
        header: "Creating Screener",
        description: "Building custom screener from your requirements"
    },
    [ActionNames.GetScreenerCreationStatus]: {
        header: "Checking Status",
        description: "Verifying screener creation progress"
    },
    [ActionNames.GetSymbolsStrikesInfo]: {
        header: "Fetching Strikes",
        description: "Loading strike prices and expiry data"
    },
    [ActionNames.GetLtp]: {
        header: "Getting Price",
        description: "Fetching last traded price information"
    },
    [ActionNames.GetOi]: {
        header: "Loading OI",
        description: "Retrieving open interest data for symbols"
    },
    [ActionNames.GetSymbolsStrikesPcrMaxPain]: {
        header: "Calculating PCR",
        description: "Computing PCR and max pain values"
    },
    [ActionNames.GetScripDetailsByIsin]: {
        header: "Fetching Details",
        description: "Loading security details using ISIN code"
    },
    [ActionNames.SwitchAssisstant]: {
        header: "Switching Mode",
        description: "Changing to different AI assistant mode"
    },
    [ActionNames.Sleep]: {
        header: "Processing",
        description: "Waiting for operation to complete"
    },
    [ActionNames.UpdateThreadTitle]: {
        header: "Updating Title",
        description: "Updating the title of the conversation"
    },
    [ActionNames.ValidatePortfolYML]: {
        header: "Validating Portfolio",
        description: "Checking portfolio YAML validity"
    }
}